import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Variants } from "framer-motion";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ClipboardList,
  FlaskConical,
  Headphones,
  ListChecks,
  MessageCircle,
  MessagesSquare,
  Radar,
  ScanFace,
  Search,
  Target,
  Workflow,
} from "lucide-react";
import {
  productMatrixImageAlt,
  productMatrixImageSrc,
  productMatrixItems,
  type ProductMatrixId,
} from "./product-matrix";
import { InboxInteractiveDemo } from "../../components/inbox-demo";

const BuilderInteractiveDemo = lazy(
  () => import("../../components/builder-demo/BuilderInteractiveDemo"),
);

const TAB_ORDER: ProductMatrixId[] = [/* "claw", */ "builder", "agent"];
const PRODUCT_MATRIX_AUTO_TAB_ENABLED = false;
const PRODUCT_MATRIX_AUTO_TAB_MS = 10000;
const INBOX_DEMO_WIDTH = 1000;
const INBOX_DEMO_HEIGHT = 620;
const AGENT_STAGE_BG = "/images/product-matrix/agent-bg.png";
const BUILDER_STAGE_BG = "/images/product-matrix/builder-bg.png";

const headerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.04,
    },
  },
};

const headerChild = {
  hidden: { opacity: 0, y: 26, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.52,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

const panelVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.04,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.24, ease: [0.4, 0, 1, 1] as const },
  },
};

const lineReveal = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const listWrapVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.055, delayChildren: 0.06 },
  },
};

const listRowVariants = {
  hidden: { opacity: 0, x: -10 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.36, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const productImageShellVariants: Variants = {
  hidden: { opacity: 0, y: 28, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.58,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

/** 产品截图切换：先渐隐再渐显（无位移/缩放），AnimatePresence mode="wait" */
const mockupScreenshotVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.48, ease: [0.22, 1, 0.36, 1] as const },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.36, ease: [0.4, 0, 1, 1] as const },
  },
};

const TAB_ICONS: Record<ProductMatrixId, LucideIcon> = {
  agent: MessagesSquare,
  claw: Radar,
  builder: Workflow,
};

const HIGHLIGHT_ICONS: Record<ProductMatrixId, [LucideIcon, LucideIcon, LucideIcon]> = {
  builder: [Target, BookOpen, FlaskConical],
  agent: [ScanFace, MessageCircle, Headphones],
  claw: [Search, ClipboardList, ListChecks],
};

/** 模拟窗口标题栏下自动切换进度条（统一蓝色） */
const MATRIX_AUTO_PROGRESS_FILL =
  "bg-gradient-to-r from-[#00b7ff]/95 via-[#00b7ff] to-[#00b7ff] shadow-[0_0_16px_rgba(0,183,255,0.55)]";

const ACCENT_BADGE: Record<
  ProductMatrixId,
  { ring: string; icon: string; tabGlow: string }
> = {
  agent: {
    ring: "border-[#00b7ff]/30 bg-gradient-to-br from-[#00b7ff]/20 via-[#00b7ff]/5 to-transparent shadow-[0_0_48px_-10px_rgba(0,183,255,0.5)]",
    icon: "text-[#00b7ff]",
    tabGlow: "shadow-[0_0_24px_-4px_rgba(0,183,255,0.35)]",
  },
  claw: {
    ring: "border-teal-400/30 bg-gradient-to-br from-teal-500/18 via-emerald-500/5 to-transparent shadow-[0_0_48px_-10px_rgba(45,212,191,0.4)]",
    icon: "text-teal-100",
    tabGlow: "shadow-[0_0_24px_-4px_rgba(45,212,191,0.3)]",
  },
  builder: {
    ring: "border-violet-400/30 bg-gradient-to-br from-violet-500/20 via-fuchsia-500/5 to-transparent shadow-[0_0_48px_-10px_rgba(167,139,250,0.45)]",
    icon: "text-violet-100",
    tabGlow: "shadow-[0_0_24px_-4px_rgba(167,139,250,0.35)]",
  },
};

function mockupGlowBackground(activeId: ProductMatrixId): string {
  switch (activeId) {
    case "agent":
      return "radial-gradient(closest-side, rgba(0, 183, 255, 0.22), transparent 75%)";
    case "claw":
      return "radial-gradient(closest-side, rgba(45, 212, 191, 0.14), transparent 72%)";
    case "builder":
      return "radial-gradient(closest-side, rgba(167, 139, 250, 0.16), transparent 72%)";
  }
}

/** 截图区域上层扫光（随 activeId 重播；单条柔光带） */
function MockupImageShineSweep({ activeId }: { activeId: ProductMatrixId }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
      aria-hidden
    >
      <m.div
        key={activeId}
        className="absolute inset-y-0 w-[28%] -skew-x-12 mix-blend-screen"
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
        }}
        initial={{ left: "-40%", opacity: 0 }}
        animate={{ left: "125%", opacity: [0, 1, 0] }}
        transition={{
          left: { duration: 0.6, delay: 0.06, ease: [0.22, 1, 0.36, 1] as const },
          opacity: { duration: 0.6, delay: 0.06, times: [0, 0.45, 1] },
        }}
      />
    </div>
  );
}

/** 模拟桌面应用标题栏下方的自动切换倒计时条 */
function MockupTitleBarProgress({ progress }: { progress: number }) {
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      aria-label="产品线自动切换进度"
      className="relative z-10 h-[2px] w-full shrink-0 overflow-hidden bg-[#060608] shadow-[inset_0_-1px_0_rgba(255,255,255,0.04)] sm:h-[3px]"
    >
      <div
        className={`absolute inset-y-0 left-0 rounded-[1px] ${MATRIX_AUTO_PROGRESS_FILL}`}
        style={{ width: `${progress * 100}%` }}
      />
    </div>
  );
}

function DemoStageFallback() {
  return (
    <div
      className="h-full w-full rounded-[16px] bg-[#f3f6fd]"
      aria-hidden
    />
  );
}

function MockupStage({
  activeId,
  autoTabProgress,
}: {
  activeId: ProductMatrixId;
  autoTabProgress: number;
}) {
  const isAgent = activeId === "agent";
  const isBuilder = activeId === "builder";
  const isLiveDemo = isAgent || isBuilder;
  const stageBackground = isAgent
    ? AGENT_STAGE_BG
    : isBuilder
      ? BUILDER_STAGE_BG
      : undefined;

  return (
    <div className="relative w-full">
      <div
        className="pointer-events-none absolute -inset-3 rounded-[32px] opacity-90 blur-2xl"
        style={{ background: mockupGlowBackground(activeId) }}
      />
      <div
        className="border-default-100/70 relative flex w-full flex-col overflow-hidden rounded-xl border shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_32px_100px_rgba(0,0,0,0.55),0_0_100px_rgba(0,183,255,0.1)] sm:rounded-xl"
        style={
          isLiveDemo
            ? undefined
            : {
                backgroundColor: "#050508",
                backgroundImage: `
                  linear-gradient(135deg, rgba(0,183,255,0.04) 0%, transparent 45%),
                  linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
                `,
                backgroundSize: "100% 100%, 24px 24px, 24px 24px",
              }
        }
      >
        {PRODUCT_MATRIX_AUTO_TAB_ENABLED ? (
          <MockupTitleBarProgress progress={autoTabProgress} />
        ) : null}
        <div
          className="relative min-h-0 w-full overflow-hidden"
          style={{ minHeight: INBOX_DEMO_HEIGHT }}
        >
          <AnimatePresence initial={false} mode="wait">
            {isLiveDemo ? (
              <m.div
                key={activeId}
                variants={mockupScreenshotVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="relative z-0 flex w-full items-center justify-center px-8 py-6 will-change-[opacity] sm:px-10 sm:py-8"
                style={{
                  minHeight: INBOX_DEMO_HEIGHT,
                  backgroundImage: `url("${stageBackground}")`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }}
              >
                <div
                  className="relative z-10 overflow-visible rounded-[16px]"
                  style={{
                    width: INBOX_DEMO_WIDTH,
                    height: INBOX_DEMO_HEIGHT,
                    maxWidth: "100%",
                  }}
                >
                  <Suspense fallback={<DemoStageFallback />}>
                    {isAgent ? (
                      <InboxInteractiveDemo
                        autoPlayFirstConversation
                        className="h-full min-h-0 w-full"
                        showGroupPanel={false}
                      />
                    ) : (
                      <BuilderInteractiveDemo
                        autoPlay
                        className="h-full min-h-0 w-full"
                      />
                    )}
                  </Suspense>
                </div>
              </m.div>
            ) : (
              <m.img
                key={activeId}
                src={productMatrixImageSrc(activeId)}
                alt={productMatrixImageAlt[activeId]}
                variants={mockupScreenshotVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="relative z-0 h-full w-full object-cover object-top will-change-[opacity]"
                style={{ minHeight: INBOX_DEMO_HEIGHT, height: INBOX_DEMO_HEIGHT }}
                loading="lazy"
                decoding="async"
              />
            )}
          </AnimatePresence>
          {!isLiveDemo ? <MockupImageShineSweep activeId={activeId} /> : null}
        </div>
      </div>
    </div>
  );
}

export default function ProductMatrixSection() {
  const [activeId, setActiveId] = useState<ProductMatrixId>("builder");
  const [autoTabProgress, setAutoTabProgress] = useState(0);
  const autoIntervalRef = useRef<number | null>(null);
  const tabListRef = useRef<HTMLDivElement>(null);

  const activeItem = useMemo(
    () => productMatrixItems.find((i) => i.id === activeId)!,
    [activeId],
  );

  const clearAutoAdvance = useCallback(() => {
    if (autoIntervalRef.current != null) {
      window.clearInterval(autoIntervalRef.current);
      autoIntervalRef.current = null;
    }
  }, []);

  const startAutoAdvance = useCallback(() => {
    clearAutoAdvance();
    if (!PRODUCT_MATRIX_AUTO_TAB_ENABLED) return;
    autoIntervalRef.current = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setActiveId((cur) => TAB_ORDER[(TAB_ORDER.indexOf(cur) + 1) % TAB_ORDER.length]);
    }, PRODUCT_MATRIX_AUTO_TAB_MS);
  }, [clearAutoAdvance]);

  const selectProductMatrixTab = useCallback(
    (id: ProductMatrixId) => {
      setActiveId(id);
      startAutoAdvance();
    },
    [startAutoAdvance],
  );

  useEffect(() => {
    startAutoAdvance();
    return () => clearAutoAdvance();
  }, [startAutoAdvance, clearAutoAdvance]);

  /** 与自动切 Tab 周期对齐的底部进度条（切换 activeId 时归零重跑） */
  useEffect(() => {
    if (!PRODUCT_MATRIX_AUTO_TAB_ENABLED) {
      setAutoTabProgress(0);
      return;
    }
    setAutoTabProgress(0);
    const start = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / PRODUCT_MATRIX_AUTO_TAB_MS);
      setAutoTabProgress(p);
      if (p < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [activeId]);

  /** 选中 Tab 被横向裁切时，滚入可视区域直至完全露出 */
  useLayoutEffect(() => {
    const root = tabListRef.current;
    if (!root) return;
    const selected = root.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!selected) return;

    const rootRect = root.getBoundingClientRect();
    const tabRect = selected.getBoundingClientRect();
    const pad = 1;
    const fullyVisible =
      tabRect.left >= rootRect.left - pad && tabRect.right <= rootRect.right + pad;

    if (!fullyVisible) {
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      selected.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeId]);

  const onTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const idx = TAB_ORDER.indexOf(activeId);
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        selectProductMatrixTab(TAB_ORDER[(idx + 1) % TAB_ORDER.length]);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        selectProductMatrixTab(TAB_ORDER[(idx - 1 + TAB_ORDER.length) % TAB_ORDER.length]);
      }
    },
    [activeId, selectProductMatrixTab],
  );

  return (
    <section
      id="product-matrix"
      className="relative mx-auto w-full max-w-6xl scroll-mt-[calc(var(--navbar-height)+var(--intl-notice-height,0px)+12px)] px-6 py-20 sm:py-28 lg:px-8 lg:py-36"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-[18%] h-[380px] -translate-y-1/2 opacity-80"
        aria-hidden
      >
        <div
          className="mx-auto h-full max-w-3xl rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(51, 142, 247, 0.1), transparent 70%)",
          }}
        />
      </div>

      <LazyMotion features={domAnimation}>
        <m.div
          className="relative mb-10 flex flex-col items-center text-center sm:mb-12"
          variants={headerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10% 0px -8% 0px" }}
        >
          <m.h2
            variants={headerChild}
            className="max-w-4xl text-foreground text-2xl font-bold tracking-tight sm:text-3xl sm:leading-[1.25] lg:text-[2rem]"
          >
            Agent 推进成交，Builder 让 Agent 快速上岗
          </m.h2>
          <m.p
            variants={headerChild}
            className="text-default-500 mt-4 max-w-3xl text-base leading-relaxed sm:text-lg"
          >
            Agent 进入真实客户对话持续推进销售结果；Builder 用业务语言完成搭建、训练与上线校准
          </m.p>
        </m.div>

        <m.div
          className="relative mb-8 flex w-full justify-center sm:mb-10"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8% 0px" }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            ref={tabListRef}
            role="tablist"
            aria-label="3Chat 产品线"
            onKeyDown={onTabKeyDown}
            className="border-default-100 bg-default-100/25 flex w-fit max-w-full flex-nowrap justify-center gap-0.5 overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-full border p-1 shadow-[0_0_0_1px_rgba(255,255,255,0.05)_inset] [scrollbar-width:none] [-ms-overflow-style:none] sm:gap-0 [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0"
          >
            {productMatrixItems.map((item) => {
              const selected = item.id === activeId;
              const TabIcon = TAB_ICONS[item.id];
              const glow = ACCENT_BADGE[item.id].tabGlow;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => selectProductMatrixTab(item.id)}
                  className={`relative flex w-auto shrink-0 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-[color,background-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.33,1,0.68,1)] max-sm:min-h-[40px] sm:justify-start sm:gap-2 sm:px-5 sm:text-[15px] ${
                    selected
                      ? `bg-default-foreground text-background scale-[1.02] shadow-md shadow-black/35 ${glow}`
                      : "text-default-500 hover:text-default-300 hover:scale-[1.01]"
                  }`}
                >
                  <TabIcon
                    className="size-3 shrink-0 sm:size-3.5"
                    strokeWidth={selected ? 2.1 : 2}
                    aria-hidden
                  />
                  <span className="whitespace-nowrap text-center sm:text-start">
                    {item.tabLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </m.div>

        <div className="relative mt-2 w-full min-w-0 max-w-full">
          <m.div
            className="border-default-100/50 bg-content1/25 relative overflow-hidden rounded-[18px] border sm:rounded-[18px]"
            variants={productImageShellVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
          >
            <div
              className="pointer-events-none absolute inset-0 rounded-[18px] opacity-[0.45] sm:rounded-[18px]"
              aria-hidden
              style={{
                background:
                  "repeating-linear-gradient(-12deg, transparent, transparent 48px, rgba(255,255,255,0.02) 48px, rgba(255,255,255,0.02) 49px)",
              }}
            />
            <MockupStage activeId={activeId} autoTabProgress={autoTabProgress} />
          </m.div>
        </div>

        <div className="relative mt-10 w-full min-w-0">
          <AnimatePresence mode="wait">
            <m.div
              key={activeId}
              role="tabpanel"
              aria-live="polite"
              variants={panelVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:items-start md:gap-x-[6rem]"
            >
              <m.div variants={lineReveal} className="min-w-0">
                <h3 className="text-foreground text-xl font-bold tracking-tight sm:text-2xl">
                  {activeItem.name}
                </h3>
                <p className="text-default-400 mt-1 text-md font-normal leading-snug">
                  {activeItem.subtitle}
                </p>
              </m.div>

              <m.div variants={lineReveal} className="min-w-0 text-left">
                <m.ul
                  variants={listWrapVariants}
                  className="flex w-full flex-col gap-2"
                >
                  {activeItem.highlights.map((text, i) => {
                    const RowIcon = HIGHLIGHT_ICONS[activeId][i];
                    return (
                      <m.li
                        key={`${activeId}-${i}`}
                        variants={listRowVariants}
                        className="flex items-center gap-4"
                      >
                        <RowIcon
                          className="text-default-400 size-5 shrink-0"
                          strokeWidth={1.65}
                          aria-hidden
                        />
                        <span className="text-default-600 text-md leading-relaxed">
                          {text}
                        </span>
                      </m.li>
                    );
                  })}
                </m.ul>
              </m.div>
            </m.div>
          </AnimatePresence>
        </div>
      </LazyMotion>
    </section>
  );
}
